import { Fragment, useCallback, useEffect, useState } from 'react';
import { useEffectOnce } from 'react-use';
import chip from '~/assets/icons/chip.svg';
import star from '~/assets/icons/star.svg';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import { IHandlingTuningPreset, InputAction } from '~/shared';
import { useModal } from '../../hooks';
import {
	setSaveAbsoluteIndex,
	setSaveFavoriteMaxIndex,
	setSaveNonFavoriteMaxIndex,
	setSavePresetList,
	setSaveSelectedIndexNext,
	setSaveSelectedIndexPrev,
	setSaveSelectedList,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { Divider } from '../Divider';
import { Save } from '../Save';
import { Search } from '../Search';
import { Select } from '../Select';
import {
	StyledContainer,
	StyledEmptyContainer,
	StyledFiltersContainer,
	StyledHeading,
	StyledSavedContainer,
} from './Saved.styled';

const Saved: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const saveSelectedIndex = useLayoutSelector(
		(state) => state.handlingList.saveSelectedIndex,
	);
	const saveSelectedList = useLayoutSelector(
		(state) => state.handlingList.saveSelectedList,
	);
	const saveAbsoluteIndex = useLayoutSelector(
		(state) => state.handlingList.saveAbsoluteIndex,
	);

	const modalState = useObservable(rage.modal.getCurrentModal());
	const presetList = useObservable(rage.handlingTuning.getPresets());
	const [favoriteList, setFavoriteList] = useState<IHandlingTuningPreset[]>([]);
	const [nonFavoriteList, setNonFavoriteList] = useState<
		IHandlingTuningPreset[]
	>([]);
	const [scrollOffset, setScrollOffset] = useState<number>(0);
	const [modeFilter, setModeFilter] = useState<string | null>(null);
	const [search, setSearch] = useState<string>('');

	const { handleSetupSaveModal } = useModal();

	useInputAction(InputAction.CommonMenuNavUp, () => {
		if (modalState) {
			return;
		}

		dispatch(setSaveSelectedIndexPrev());
	});

	useInputAction(InputAction.CommonMenuNavDown, () => {
		if (modalState) {
			return;
		}

		dispatch(setSaveSelectedIndexNext());
	});

	useInputAction(InputAction.CommonMenuAccept, handleSetupSaveModal);

	useInputAction(InputAction.HandlingSetFavorite, () => {
		if (modalState) {
			return;
		}

		if (presetList.length !== 0) {
			rage.handlingTuning
				.setPresetFavorite(
					saveAbsoluteIndex,
					!presetList[saveAbsoluteIndex].favorite,
				)
				.catch((err) => rage.notifications.showException(err));
		}
	});

	const handleScroll = useCallback((e: React.UIEvent<HTMLElement>) => {
		const scrollTop = (e as any).target?.scrollTop;
		if (scrollTop) {
			setScrollOffset(scrollTop);
		}
	}, []);

	const handlePreventScroll = useCallback((ev: KeyboardEvent) => {
		const { key } = ev;

		if (key === 'ArrowUp' || key === 'ArrowDown') {
			ev.preventDefault();
		}
	}, []);

	useEffect(() => {
		dispatch(setSavePresetList(presetList));
	}, [presetList]);

	useEffect(() => {
		if (!modeFilter) {
			setFavoriteList(
				presetList.filter(
					(item) => item?.favorite && item.name.includes(search),
				),
			);
			setNonFavoriteList(
				presetList.filter(
					(item) => !item?.favorite && item.name.includes(search),
				),
			);
		} else {
			setFavoriteList(
				presetList.filter(
					(item) =>
						item?.favorite &&
						item.mode === modeFilter.toLowerCase() &&
						item.name.includes(search),
				),
			);
			setNonFavoriteList(
				presetList.filter(
					(item) =>
						!item?.favorite &&
						item.mode === modeFilter.toLowerCase() &&
						item.name.includes(search),
				),
			);
		}
	}, [presetList, modeFilter, search]);

	useEffect(() => {
		dispatch(setSaveFavoriteMaxIndex(favoriteList.length - 1));
		dispatch(setSaveNonFavoriteMaxIndex(nonFavoriteList.length - 1));
		if (favoriteList.length === 0) {
			dispatch(setSaveSelectedList('nonFavorite'));
		}
	}, [favoriteList, nonFavoriteList]);

	useEffect(() => {
		const saveDiv = document.getElementById(
			`save-${saveSelectedList}-${saveSelectedIndex}`,
		);
		if (saveDiv !== null) {
			setTimeout(() => {
				saveDiv.scrollIntoView({ block: 'nearest' });
			}, 50);
		}
		if (saveSelectedList === 'favorite' && favoriteList.length > 0) {
			dispatch(
				setSaveAbsoluteIndex(
					presetList.indexOf(favoriteList[saveSelectedIndex]),
				),
			);
		} else if (
			saveSelectedList === 'nonFavorite' &&
			nonFavoriteList.length > 0
		) {
			dispatch(
				setSaveAbsoluteIndex(
					presetList.indexOf(nonFavoriteList[saveSelectedIndex]),
				),
			);
		}
	}, [saveSelectedIndex, saveSelectedList, presetList]);

	useEffectOnce(() => {
		window.addEventListener('keydown', handlePreventScroll);

		return () => window.removeEventListener('keydown', handlePreventScroll);
	});

	return (
		<StyledContainer>
			<StyledFiltersContainer>
				<Search
					placeholder={'Введите название для поиска'}
					onChange={(e) => setSearch(e.target.value)}
				/>
				<Select
					items={['Comfort', 'Sport', 'Drift']}
					placeholder={'Режим настройки'}
					onChange={(value) => {
						setModeFilter(value ? value.toLowerCase() : null);
					}}
				/>
				<Divider $direction={'horizontal'} $top={0} $bottom={10} />
			</StyledFiltersContainer>

			{presetList.length === 0 ? (
				<StyledEmptyContainer>
					<Text>
						К сожалению, у вас нет
						<br />
						сохраненных настроек ТС
					</Text>
				</StyledEmptyContainer>
			) : (
				<StyledSavedContainer onScroll={handleScroll} tabIndex={-1}>
					{favoriteList.length > 0 && (
						<Fragment>
							<StyledHeading>
								<img src={star} />
								<Text>Избранное</Text>
							</StyledHeading>
							<Divider $direction={'horizontal'} $top={0} $bottom={0} />
							{favoriteList.map((item, index) => (
								<Save
									name={item.name}
									mode={item.mode}
									info={item.info}
									author={item.author}
									creationDate={item.creationDate}
									isFavorite={item.favorite}
									list={'favorite'}
									key={index}
									index={index}
									absoluteIndex={presetList.indexOf(item)}
									selected={
										saveSelectedList === 'favorite' &&
										index === saveSelectedIndex
									}
									id={`save-favorite-${index}`}
									scrollOffset={scrollOffset}
								/>
							))}
						</Fragment>
					)}
					{nonFavoriteList.length > 0 && (
						<Fragment>
							<StyledHeading
								style={{ marginTop: favoriteList.length > 0 ? 10 : 0 }}
							>
								<img src={chip} />
								<Text>Все настройки</Text>
							</StyledHeading>
							<Divider $direction={'horizontal'} $top={0} $bottom={0} />
							{nonFavoriteList.map((item, index) => (
								<Save
									name={item.name}
									mode={item.mode}
									info={item.info}
									author={item.author}
									creationDate={item.creationDate}
									isFavorite={item?.favorite}
									list={'nonFavorite'}
									key={index}
									index={index}
									absoluteIndex={presetList.indexOf(item)}
									selected={
										saveSelectedList === 'nonFavorite' &&
										index === saveSelectedIndex
									}
									id={`save-nonFavorite-${index}`}
									scrollOffset={scrollOffset}
								/>
							))}
						</Fragment>
					)}
				</StyledSavedContainer>
			)}
		</StyledContainer>
	);
};

export { Saved };
